import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  approveCuratorPost,
  fetchCuratorPostById,
  fetchCuratorPosts,
  type ApprovalFilter,
  type CuratorIgPostDetailData,
  type CuratorIgPostListData,
} from '@/services/slaydAPI/posts';
import useLayoutStore from '@/stores/useLayoutStore';
import styles from './ApprovalPortalPage.module.css';
import { PortalHeader } from './components/PortalHeader';
import { PostDetail } from './components/PostDetail';
import { PostList } from './components/PostList';

type ApprovalStatus = 'idle' | 'approving' | 'success' | 'error';

const FILTER_OPTIONS: { value: ApprovalFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const ApprovalPortalPage = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchType, setSearchType] = useState<'id' | 'name' | 'title'>(
    searchParams.get('title') ? 'title' : searchParams.get('curator_name') ? 'name' : 'id'
  );
  const [searchValue, setSearchValue] = useState(
    searchParams.get('title') || searchParams.get('curator_name') || ''
  );
  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );

  useEffect(() => {
    setNavbarVisibility(false);
    return () => {
      setNavbarVisibility(true);
    };
  }, [setNavbarVisibility]);

  const [posts, setPosts] = useState<CuratorIgPostListData[]>([]);
  const [selectedPost, setSelectedPost] =
    useState<CuratorIgPostDetailData | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>('idle');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const activeFilter: ApprovalFilter =
    (searchParams.get('filter') as ApprovalFilter) || 'all';
  const activeCuratorSearch = searchParams.get('curator_name') || '';
  const activeTitleSearch = searchParams.get('title') || '';

  const setFilter = (f: ApprovalFilter): void => {
    const params = new URLSearchParams(searchParams);
    if (f === 'all') {
      params.delete('filter');
    } else {
      params.set('filter', f);
    }
    setSearchParams(params);
    setPage(1);
  };

  const fetchPosts = useCallback(async (): Promise<void> => {
    setLoadingList(true);
    setError(null);
    try {
      const data = await fetchCuratorPosts(
        activeFilter,
        page,
        50,
        activeCuratorSearch,
        activeTitleSearch
      );
      setPosts(data.results);
      setTotalCount(data.pagination.totalItems);
      setHasNext(data.pagination.hasNext);
      setHasPrevious(data.pagination.hasPrevious);
    } catch {
      setError('Failed to fetch posts');
      setPosts([]);
    } finally {
      setLoadingList(false);
    }
  }, [activeFilter, page, activeCuratorSearch, activeTitleSearch]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (id) {
      setLoadingDetail(true);
      fetchCuratorPostById(id)
        .then((data) => {
          setSelectedPost(data);
          setApprovalStatus('idle');
        })
        .catch(() => {
          setSelectedPost(null);
          setError('Failed to fetch post detail');
        })
        .finally(() => setLoadingDetail(false));
    } else {
      setSelectedPost(null);
    }
  }, [id]);

  const handleSelectPost = (postId: number): void => {
    window.history.pushState(null, '', `/posts-approve/${postId}`);
    setLoadingDetail(true);
    setError(null);
    fetchCuratorPostById(postId)
      .then((data) => {
        setSelectedPost(data);
        setApprovalStatus('idle');
      })
      .catch(() => {
        setSelectedPost(null);
        setError('Failed to fetch post detail');
      })
      .finally(() => setLoadingDetail(false));
  };

  const handleApprove = async (approved: boolean): Promise<void> => {
    if (!selectedPost) return;
    setApprovalStatus('approving');
    try {
      const result = await approveCuratorPost(selectedPost.id, approved);
      setSelectedPost((prev) =>
        prev ? { ...prev, isApproved: result.isApproved } : prev
      );
      setPosts((prev) =>
        prev.map((p) =>
          p.id === selectedPost.id ? { ...p, isApproved: result.isApproved } : p
        )
      );
      setApprovalStatus('success');
      setTimeout(() => setApprovalStatus('idle'), 2000);
    } catch {
      setApprovalStatus('error');
      setTimeout(() => setApprovalStatus('idle'), 3000);
    }
  };

  const handleSearch = (): void => {
    if (searchType === 'id') {
      if (searchValue.trim()) {
        navigate(`/posts-approve/${searchValue.trim()}`);
        setSearchValue('');
      }
    } else {
      const params = new URLSearchParams(searchParams);
      if (searchType === 'title') {
        params.delete('curator_name');
        if (searchValue.trim()) {
          params.set('title', searchValue.trim());
        } else {
          params.delete('title');
        }
      } else if (searchType === 'name') {
        params.delete('title');
        if (searchValue.trim()) {
          params.set('curator_name', searchValue.trim());
        } else {
          params.delete('curator_name');
        }
      }
      setSearchParams(params);
      setPage(1);
    }
  };

  const getStatusClass = (isApproved: boolean | null): string => {
    if (isApproved === true) return styles.approved;
    if (isApproved === false) return styles.rejected;
    return styles.pending;
  };

  const getStatusLabel = (isApproved: boolean | null): string => {
    if (isApproved === true) return 'Approved';
    if (isApproved === false) return 'Rejected';
    return 'Pending';
  };

  const products = selectedPost?.products || [];

  return (
    <div className={styles.page}>
      <PortalHeader
        searchType={searchType}
        setSearchType={setSearchType}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        activeFilter={activeFilter}
        filterOptions={FILTER_OPTIONS}
        totalCount={totalCount}
        setFilter={setFilter}
        onSearch={handleSearch}
      />

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.workspace}>
        <PostList
          getStatusClass={getStatusClass}
          getStatusLabel={getStatusLabel}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          loadingList={loadingList}
          page={page}
          posts={posts}
          selectedPostId={selectedPost?.id}
          onNextPage={() => setPage((p) => p + 1)}
          onPrevPage={() => setPage((p) => p - 1)}
          onSelectPost={handleSelectPost}
        />

        <PostDetail
          approvalStatus={approvalStatus}
          loadingDetail={loadingDetail}
          products={products}
          selectedPost={selectedPost}
          handleApprove={handleApprove}
        />
      </div>
    </div>
  );
};

export default ApprovalPortalPage;
